/**
 * Render Scheduler - Determines when nodes need content updates.
 *
 * This module encapsulates the logic for deciding when to re-render
 * node content, separate from the actual rendering. This makes the
 * logic unit-testable without DOM dependencies.
 *
 * Key responsibilities:
 * - Track zoom changes across render frames
 * - Detect node state changes
 * - Decide when content updates are needed
 * - Handle texture rendering mode transitions
 *
 * The core insight is that texture-rendered nodes ALWAYS need their
 * renderer called every frame (to add to the WebGL batch), even when
 * nothing has changed. The renderer manages the texture vs HTML decision.
 */

import type { LODLevel } from './renderer';

/** Node state for change detection */
export interface NodeRenderState {
	positionX: number;
	positionY: number;
	width: number;
	height: number;
	z: number;
	type: string;
	selected: boolean;
	hovered: boolean;
	dragging: boolean;
	resizing: boolean;
	className: string | undefined;
	dataHash: string;
	/** The LOD level from last render */
	lod: LODLevel | undefined;
	/** Whether node is currently texture-rendered */
	isTextureRendered: boolean;
	/** Node rotation in radians */
	rotation: number;
}

/** Input for render decision */
export interface RenderDecisionInput {
	nodeId: string;
	isNewElement: boolean;
	currentState: NodeRenderState;
	cachedState: NodeRenderState | undefined;
	zoomChanged: boolean;
	optionsChanged: boolean;
}

/** Output from render decision */
export interface RenderDecisionOutput {
	/** Whether to update layout (position, size, styles) */
	updateLayout: boolean;
	/**
	 * Whether to call the renderer this frame.
	 *
	 * Note: This does NOT mean "content needs regeneration". For texture-rendered
	 * nodes, the renderer must be called every frame to add the cached texture
	 * to the WebGL draw batch - even though the texture itself doesn't change.
	 */
	callRenderer: boolean;
	/** Reason for the decision (for debugging) */
	reason: string;
}

/**
 * Determine if a node's renderer should be called this frame.
 *
 * This is the core logic extracted for testability.
 *
 * Key rules:
 * 1. New elements ALWAYS need renderer call
 * 2. Changed nodes (position, data, etc) need renderer call
 * 3. Zoom changes need renderer call (LOD may change)
 * 4. Options changes need renderer call
 * 5. Texture-rendered nodes ALWAYS need renderer call (to add to WebGL batch)
 * 6. Nodes in texture-candidate LOD levels need renderer call (may switch to texture)
 */
export function computeRenderDecision(input: RenderDecisionInput): RenderDecisionOutput {
	const { isNewElement, currentState, cachedState, zoomChanged, optionsChanged } = input;

	// Rule 1: New elements always need full update
	if (isNewElement) {
		return {
			updateLayout: true,
			callRenderer: true,
			reason: 'new_element'
		};
	}

	// Check if node state has changed
	const nodeHasChanged = cachedState ? hasNodeChanged(currentState, cachedState) : true;

	// Rule 2: Changed nodes need update
	if (nodeHasChanged) {
		return {
			updateLayout: true,
			callRenderer: true,
			reason: 'node_changed'
		};
	}

	// Rule 3: Zoom changes need renderer call (but not layout)
	if (zoomChanged) {
		return {
			updateLayout: false,
			callRenderer: true,
			reason: 'zoom_changed'
		};
	}

	// Rule 4: Options changes need renderer call
	if (optionsChanged) {
		return {
			updateLayout: false,
			callRenderer: true,
			reason: 'options_changed'
		};
	}

	// Rule 5: Texture-rendered nodes ALWAYS need renderer call
	// This is critical - the renderer needs to be called every frame
	// to add the texture to the WebGL batch for drawing
	if (currentState.isTextureRendered) {
		return {
			updateLayout: false,
			callRenderer: true,
			reason: 'texture_rendered'
		};
	}

	// Rule 6: Nodes at texture-candidate LOD levels need renderer call
	// These are nodes that MIGHT be texture rendered but haven't been captured yet
	// or are in transition. Include ALL simplified LOD levels, not just title/icon.
	const lod = currentState.lod;
	if (lod === 'title' || lod === 'icon' || lod === 'dot') {
		return {
			updateLayout: false,
			callRenderer: true,
			reason: 'texture_candidate_lod'
		};
	}

	// No renderer call needed
	return {
		updateLayout: false,
		callRenderer: false,
		reason: 'no_change'
	};
}

/**
 * Check if node state has changed.
 */
export function hasNodeChanged(current: NodeRenderState, cached: NodeRenderState): boolean {
	return (
		current.positionX !== cached.positionX ||
		current.positionY !== cached.positionY ||
		current.width !== cached.width ||
		current.height !== cached.height ||
		current.z !== cached.z ||
		current.type !== cached.type ||
		current.selected !== cached.selected ||
		current.hovered !== cached.hovered ||
		current.dragging !== cached.dragging ||
		current.className !== cached.className ||
		current.dataHash !== cached.dataHash ||
		current.rotation !== cached.rotation
	);
}

/**
 * RenderScheduler class - tracks state across frames for multiple nodes.
 *
 * This is the stateful wrapper around the pure decision functions.
 */
export class RenderScheduler {
	/** Cached state per node */
	private nodeStates: Map<string, NodeRenderState> = new Map();

	/** Zoom from last completed render */
	private lastRenderedZoom: number = 1;

	/** Whether zoom has changed since last render */
	private zoomChangedSinceRender: boolean = true; // Start true for initial render

	/** Whether render options changed */
	private optionsChanged: boolean = true; // Start true for initial render

	/**
	 * Notify of a camera zoom change.
	 * Called from setCamera - may be called multiple times before render.
	 */
	onZoomChange(newZoom: number): void {
		// Track zoom change relative to last RENDERED zoom, not last setCamera call
		// This handles rapid zoom events where multiple setCamera calls happen before render
		if (newZoom !== this.lastRenderedZoom) {
			this.zoomChangedSinceRender = true;
		}
	}

	/**
	 * Notify that render options have changed.
	 */
	onOptionsChange(): void {
		this.optionsChanged = true;
	}

	/**
	 * Get whether zoom changed since last render.
	 */
	hasZoomChanged(): boolean {
		return this.zoomChangedSinceRender;
	}

	/**
	 * Get whether options changed since last render.
	 */
	hasOptionsChanged(): boolean {
		return this.optionsChanged;
	}

	/**
	 * Compute render decision for a node.
	 */
	computeDecision(
		nodeId: string,
		isNewElement: boolean,
		currentState: NodeRenderState
	): RenderDecisionOutput {
		const cachedState = this.nodeStates.get(nodeId);

		return computeRenderDecision({
			nodeId,
			isNewElement,
			currentState,
			cachedState,
			zoomChanged: this.zoomChangedSinceRender,
			optionsChanged: this.optionsChanged
		});
	}

	/**
	 * Update cached state for a node after rendering.
	 */
	updateNodeState(nodeId: string, state: NodeRenderState): void {
		this.nodeStates.set(nodeId, state);
	}

	/**
	 * Remove cached state for a node.
	 */
	removeNodeState(nodeId: string): void {
		this.nodeStates.delete(nodeId);
	}

	/**
	 * Get cached state for a node.
	 */
	getNodeState(nodeId: string): NodeRenderState | undefined {
		return this.nodeStates.get(nodeId);
	}

	/**
	 * Called at end of render frame to reset per-frame flags.
	 * @param currentZoom The zoom level at which rendering completed
	 */
	endFrame(currentZoom: number): void {
		this.lastRenderedZoom = currentZoom;
		this.zoomChangedSinceRender = false;
		this.optionsChanged = false;
	}

	/**
	 * Get the last rendered zoom level.
	 */
	getLastRenderedZoom(): number {
		return this.lastRenderedZoom;
	}

	/**
	 * Clear all cached state.
	 */
	clear(): void {
		this.nodeStates.clear();
		this.lastRenderedZoom = 1;
		this.zoomChangedSinceRender = true;
		this.optionsChanged = true;
	}
}

/**
 * Create a new RenderScheduler instance.
 */
export function createRenderScheduler(): RenderScheduler {
	return new RenderScheduler();
}
